/**
 * 見積書 (quotes) の hooks。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §4.4, §8
 *
 * - 一覧読込は Firestore collectionGroup で全 deal 横断取得
 * - 書込 (createQuote / sendQuoteMail) は Callable 経由
 */
import type {
  PublicQuoteView,
  Quote,
  QuoteInstallments,
  QuotePaymentMethod,
  QuoteTradeInType,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '@/lib/firebase';

export interface QuoteRecord extends Quote {
  id: string;
}

export function useQuotes() {
  return useQuery({
    queryKey: ['quotes', 'all'],
    queryFn: async (): Promise<QuoteRecord[]> => {
      const snap = await getDocs(
        query(collectionGroup(db, 'quotes'), orderBy('createdAt', 'desc')),
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Quote) }));
    },
  });
}

export function useDealQuotes(dealId: string | null | undefined) {
  return useQuery({
    queryKey: ['quotes', 'deal', dealId],
    enabled: !!dealId,
    queryFn: async (): Promise<QuoteRecord[]> => {
      if (!dealId) return [];
      const snap = await getDocs(
        query(collection(db, 'deals', dealId, 'quotes'), orderBy('createdAt', 'desc')),
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Quote) }));
    },
  });
}

export function useQuote(dealId: string | null, quoteId: string | null) {
  return useQuery({
    queryKey: ['quotes', 'detail', dealId, quoteId],
    enabled: !!dealId && !!quoteId,
    queryFn: async (): Promise<QuoteRecord | null> => {
      if (!dealId || !quoteId) return null;
      const snap = await getDoc(doc(db, 'deals', dealId, 'quotes', quoteId));
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as Quote) };
    },
  });
}

// ─── Callable: createQuote ───
const createQuoteFn = httpsCallable<
  {
    dealId: string;
    title?: string;
    productListId?: string | null;
    lineItems: Array<{
      name: string;
      modelName: string;
      variation?: string | null;
      sku?: string | null;
      priceExcludingTax: number;
      priceIncludingTax: number;
      quantity: number;
      /** 行割引 (¥固定額 / %)。税抜小計に適用 */
      discountType?: 'amount' | 'percent' | null;
      discountValue?: number | null;
    }>;
    paymentMethod: QuotePaymentMethod;
    installments?: QuoteInstallments | null;
    hasTradeIn: boolean;
    tradeInType?: QuoteTradeInType | null;
    tradeInAmount?: number | null;
    assigneeUid?: string | null;
    remarks?: string | null;
    /** true なら書類上で割引金額を非表示にする */
    hideDiscounts?: boolean | null;
    /** true なら書類上で品目名の下の型番を非表示にする */
    hideModelNames?: boolean | null;
    sendMail?: boolean;
    recipientEmail?: string | null;
    /** 発行日の手動上書き（ISO 文字列、例: "2026-05-06"）。バックデート対応。 */
    issuedAtOverride?: string | null;
    /** 有効期限の手動上書き（ISO 文字列）。 */
    validUntilOverride?: string | null;
    /** T2: 請求先住所 ID。未指定は顧客の既定請求先。 */
    billingAddressId?: string | null;
    /** T2: 納品先住所 ID。`'__NONE__'` で「納品先なし」を明示。未指定は顧客の既定。 */
    deliveryAddressId?: string | null;
  },
  {
    dealId: string;
    quoteId: string;
    publicToken: string;
    totalIncludingTax: number;
    mailSent: boolean;
    mailReason?: string;
  }
>(functions, 'createQuote');

export function useCreateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof createQuoteFn>[0]) => {
      const r = await createQuoteFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      if (!vars) return;
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.invalidateQueries({ queryKey: ['quotes', 'deal', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealId] });
    },
  });
}

/**
 * 既存見積書の発行日 / 有効期限を編集する (v3.1.6 追加)。
 * 作成済みの見積書に対してバックデート発行や有効期限の延長を行う用途。
 * Firestore に直接書き込み (Callable は不要 — editor 以上の write 権限ルールで保護)。
 */
export function useUpdateQuoteDates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      dealId: string;
      quoteId: string;
      issuedAt?: Date | null;
      validUntil?: Date | null;
    }) => {
      const ref = doc(db, 'deals', input.dealId, 'quotes', input.quoteId);
      const update: Record<string, unknown> = {
        updatedAt: serverTimestamp(),
      };
      if (input.issuedAt !== undefined) {
        update.issuedAt = input.issuedAt ? Timestamp.fromDate(input.issuedAt) : null;
      }
      if (input.validUntil !== undefined) {
        update.validUntil = input.validUntil ? Timestamp.fromDate(input.validUntil) : null;
      }
      await updateDoc(ref, update);
      return input;
    },
    onSuccess: (vars) => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.invalidateQueries({ queryKey: ['quotes', 'deal', vars.dealId] });
      qc.invalidateQueries({
        queryKey: ['quotes', 'detail', vars.dealId, vars.quoteId],
      });
    },
  });
}

// ─── Callable: updateQuoteDraft (下書きの編集) ───
const updateQuoteDraftFn = httpsCallable<
  {
    dealId: string;
    quoteId: string;
    title?: string;
    lineItems: Array<{
      name: string;
      modelName: string;
      variation?: string | null;
      sku?: string | null;
      priceExcludingTax: number;
      priceIncludingTax: number;
      quantity: number;
      /** 行割引 (¥固定額 / %)。税抜小計に適用 */
      discountType?: 'amount' | 'percent' | null;
      discountValue?: number | null;
    }>;
    paymentMethod: QuotePaymentMethod;
    installments?: QuoteInstallments | null;
    hasTradeIn: boolean;
    tradeInType?: QuoteTradeInType | null;
    tradeInAmount?: number | null;
    remarks?: string | null;
    /** true なら書類上で割引金額を非表示にする */
    hideDiscounts?: boolean | null;
    /** true なら書類上で品目名の下の型番を非表示にする */
    hideModelNames?: boolean | null;
    /** T2: 請求先住所 ID (未指定 = 現在のスナップショットを維持) */
    billingAddressId?: string | null;
    /** T2: 納品先住所 ID。`'__NONE__'` で「納品先なし」を明示 */
    deliveryAddressId?: string | null;
  },
  { dealId: string; quoteId: string; totalIncludingTax: number }
>(functions, 'updateQuoteDraft');

export function useUpdateQuoteDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof updateQuoteDraftFn>[0]) => {
      const r = await updateQuoteDraftFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      if (!vars) return;
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.invalidateQueries({ queryKey: ['quotes', 'deal', vars.dealId] });
      qc.invalidateQueries({
        queryKey: ['quotes', 'detail', vars.dealId, vars.quoteId],
      });
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealId] });
    },
  });
}

// ─── Callable: markQuoteWon (v3.1.7) ───
const markQuoteWonFn = httpsCallable<
  {
    dealId: string;
    quoteId: string;
    finalAmountOverride?: number | null;
    memo?: string;
  },
  {
    dealId: string;
    quoteId: string;
    invoiceId: string;
    invoiceNumber: string;
    finalAmount: number;
    dealNewlyWon: boolean;
  }
>(functions, 'markQuoteWon');

export function useMarkQuoteWon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NonNullable<Parameters<typeof markQuoteWonFn>[0]>) => {
      const r = await markQuoteWonFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.invalidateQueries({ queryKey: ['quotes', 'deal', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoices', 'deal', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealId] });
    },
  });
}

// ─── Callable: markQuoteLost (v3.1.7) ───
const markQuoteLostFn = httpsCallable<
  {
    dealId: string;
    quoteId: string;
    lostReason: string;
    memo?: string;
  },
  { dealId: string; quoteId: string }
>(functions, 'markQuoteLost');

export function useMarkQuoteLost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NonNullable<Parameters<typeof markQuoteLostFn>[0]>) => {
      const r = await markQuoteLostFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.invalidateQueries({ queryKey: ['quotes', 'deal', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealId] });
    },
  });
}

export interface SendDocumentMailOverrides {
  to?: string | null;
  fromAccountId?: string | null;
  cc?: string[] | null;
  subject?: string | null;
  body?: string | null;
}

const sendQuoteMailFn = httpsCallable<
  {
    dealId: string;
    quoteId: string;
    recipientEmail?: string | null;
  } & SendDocumentMailOverrides,
  { quoteId: string; mailSent: boolean; reason?: string }
>(functions, 'sendQuoteMail');

export function useSendQuoteMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof sendQuoteMailFn>[0]) => {
      const r = await sendQuoteMailFn(input);
      return r.data;
    },
    onSuccess: (_d, vars) => {
      if (!vars) return;
      qc.invalidateQueries({ queryKey: ['quotes', 'deal', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['quotes', 'detail', vars.dealId, vars.quoteId] });
    },
  });
}

/** 送信元プルダウンの候補（外部SA は 1 件に制限される）。 */
export interface MailAccountOption {
  id: string;
  displayName: string;
  email: string;
}

export interface PrepareDocumentMailResult {
  defaultTo: string;
  fromAccountId: string | null;
  subject: string;
  body: string;
  publicUrl: string;
  /**
   * null / undefined = 制限なし（listMailAccounts の全件から選択）。
   * 外部SA の場合はシステムが実際に使う 1 件のみが返る。
   */
  fromAccountOptions?: MailAccountOption[] | null;
}

const prepareQuoteMailFn = httpsCallable<
  { dealId: string; quoteId: string },
  PrepareDocumentMailResult
>(functions, 'prepareQuoteMail');

/** 見積メール編集モーダルの初期値を取得（送信元/件名/本文/公開URL）。 */
export function usePrepareQuoteMail() {
  return useMutation({
    mutationFn: async (input: { dealId: string; quoteId: string }) => {
      const r = await prepareQuoteMailFn(input);
      return r.data;
    },
  });
}

const getPublicQuoteFn = httpsCallable<
  { token: string },
  { status: 'ok'; quote: PublicQuoteView } | { status: 'expired' } | { status: 'not_found' }
>(functions, 'getPublicQuote');

export function usePublicQuote(token: string | null) {
  return useQuery({
    queryKey: ['public_quote', token],
    enabled: !!token,
    queryFn: async () => {
      if (!token) return null;
      const r = await getPublicQuoteFn({ token });
      return r.data;
    },
  });
}
